import React, { useState } from 'react';
import { CSVFile, DetectedFormat } from '../../types/data/csv';
import './CSVPreview.css';

interface CSVPreviewProps {
  file: CSVFile;
}

const CSVPreview: React.FC<CSVPreviewProps> = ({ file }) => {
  const [showFullData, setShowFullData] = useState(false);
  const dataToShow = showFullData ? file.rows : file.previewData;
  const rowCount = file.rows.length;
  const columnCount = file.columns.length;

  return (
    <div className="csv-preview">
        <div className="file-info compact">
          <div className="file-header compact">
            <h4 className="compact-filename">{file.name}</h4>
            <div className="compact-summary">
              <strong>{rowCount} rows</strong> and <strong>{columnCount} columns</strong> of data.
            </div>
          </div>
        </div>
        
        <div className="preview-container">
          <div className="preview-header">
            <h4>Data Preview</h4>
            <button 
              className="toggle-view-button"
              onClick={() => setShowFullData(!showFullData)}
            >
              {showFullData ? 'Show Less' : 'Show More'}
            </button>
          </div>
        
        <div className="preview-table-container">
          <table className="preview-table">
            <thead>
              <tr>
                <th className="row-header">#</th>
                {file.columns.map((column, index) => (
                  <th key={index} title={column}>
                    {column.length > 20 ? column.substring(0, 20) + '...' : column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataToShow.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="row-header">{rowIndex + 1}</td>
                  {row.map((cell, cellIndex) => {
                    const cellContent = cell?.toString() || '';
                    const displayContent = cellContent.length > 50 
                      ? cellContent.substring(0, 50) + '...' 
                      : cellContent;
                    
                    return (
                      <td 
                        key={cellIndex} 
                        title={cellContent}
                        className={isNumeric(cellContent) ? 'numeric-cell' : ''}
                      >
                        {displayContent || <span className="empty-cell">Empty</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {!showFullData && rowCount > file.previewData.length && (
          <div className="more-rows-message">
            + {rowCount - file.previewData.length} more rows
            <button 
              className="show-more-button"
              onClick={() => setShowFullData(true)}
            >
              Show All
            </button>
          </div>
        )}
        </div>
    </div>
  );
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to get a nice label for the file type
const getFileTypeLabel = (fileType: 'csv' | 'xlsx' | 'xls'): string => {
  switch (fileType) {
    case 'csv':
      return 'CSV File';
    case 'xlsx':
      return 'Excel File (XLSX)';
    case 'xls':
      return 'Excel File (XLS)';
    default:
      return 'Unknown File Type';
  }
};

// Helper function to check if a value is numeric
const isNumeric = (value: string): boolean => {
  if (!value) return false;
  return !isNaN(Number(value.replace(/[$,]/g, '')));
};

export default CSVPreview; 